import {
  queryDevice,
  queryDevices,
  queryDeviceEvents,
  queryDeviceUptime,
  updateOwner,
  createConfigTemplate,
  updateConfigTemplate,
  deleteConfigTemplate,
  queryAggregations,
  updateConfigV2,
  queryConfigTemplates,
  applyConfigTemplate,
  createSprinterJob,
} from "@/api/device";
import { currentUtcMonth } from "@/lib/uptime";

const AGGREGATIONS_TTL_MS = 30 * 1000;
let aggregationsInFlight = null;
let aggregationsFetchedAt = null;
let devicesInFlight = null;
let devicesRequestId = 0;

const state = () => ({
  all: new Map(),
  current: {},
  loading: false,
  loadingDevices: false,
  loadingAggregations: false,
  loadingConfigTemplates: false,
  loadingEvents: false,
  hasNextPage: false,
  nextPageToken: "",
  eventsHasNextPage: false,
  eventsNextPageToken: null,
  events: [],
  eventsError: false,
  deviceStateAggregations: {},
  alarmAggregations: {},
  searchResult: new Map(),
  // Uptime of the current UTC month; serialNumber tells which device it belongs to.
  uptime: null,
});

const actions = {
  async getDevices({ commit, state, rootState }, forceReload) {
    if (!forceReload && devicesInFlight) {
      return devicesInFlight;
    }

    if (forceReload) {
      commit("setHasNextPage", false);
      commit("setNextPageToken", null);
      commit("setDevices", new Map());
    }

    commit("setLoadingDevices", true);

    const requestId = ++devicesRequestId;
    const nextPageToken = state.nextPageToken ? state.nextPageToken : null;
    const queryData = rootState.searchQueryData;

    const request = (async () => {
      try {
        const result = await queryDevices(nextPageToken, queryData, 30);

        if (!result) {
          return;
        }

        if (requestId !== devicesRequestId) {
          return;
        }

        commit("setHasNextPage", result.hasNextPage);
        commit("setNextPageToken", result.nextPageToken);

        if (forceReload) {
          commit("setDevices", new Map());
        }
        commit("appendDevices", result.devices);
      } finally {
        if (requestId === devicesRequestId) {
          commit("setLoadingDevices", false);
        }
        if (devicesInFlight === request) {
          devicesInFlight = null;
        }
      }
    })();

    devicesInFlight = request;
    return request;
  },

  async searchDeviceById({ commit }, search) {
    commit("setLoadingDevices", true);
    const result = await queryDevices(null, search);
    commit("setSearchResult", result.devices);

    let devices = [];
    if (state.all && state.all.size > 0) {
      devices = Array.from(state.all.values()).concat(result.devices);
    }
    commit("setDevices", devices.length > 0 ? devices : result.devices);

    commit("setLoadingDevices", false);
  },

  async clearSearchResult({ commit }) {
    commit("setSearchResult", new Map());
  },

  async getAggregations({ commit }, { refresh = false } = {}) {
    if (!refresh) {
      if (aggregationsFetchedAt !== null && Date.now() - aggregationsFetchedAt < AGGREGATIONS_TTL_MS) {
        return;
      }
      if (aggregationsInFlight) {
        return aggregationsInFlight;
      }
    }

    commit("setLoadingAggregations", true);

    const request = (async () => {
      try {
        const aggregation = await queryAggregations();

        if (aggregation && aggregation.deviceStateAggregations) {
          commit("setDeviceStateAggregations", aggregation.deviceStateAggregations);
        }
        if (aggregation && aggregation.deviceAlarmsAggregations) {
          commit("setAlarmAggregations", aggregation.deviceAlarmsAggregations);
        }
        if (aggregation) {
          aggregationsFetchedAt = Date.now();
        }
      } finally {
        if (aggregationsInFlight === request) {
          aggregationsInFlight = null;
        }
        commit("setLoadingAggregations", false);
      }
    })();

    aggregationsInFlight = request;
    return request;
  },

  async getDevice({ commit, dispatch }, serialNumber) {
    commit("setLoadingDevices", true);

    const device = await queryDevice(serialNumber);

    commit("setDevice", device);
    commit("setLoadingDevices", false);
    dispatch("getDeviceUptime", serialNumber);
  },

  async reloadDevice({ commit, dispatch }, serialNumber) {
    commit("setLoadingDevices", true);
    const device = await queryDevice(serialNumber);
    commit("setDevice", device);
    commit("setLoadingDevices", false);
    dispatch("getDeviceUptime", serialNumber);
    return true;
  },

  async getDeviceUptime({ commit, state }, serialNumber) {
    const { start, end } = currentUtcMonth();
    const uptime = await queryDeviceUptime(serialNumber, start, end);
    // A late answer for a device that is no longer open must not replace the current one.
    if (state.current.serialNumber !== serialNumber) {
      return;
    }
    commit("setUptime", uptime ? { ...uptime, serialNumber } : null);
  },

  async reloadDeviceOwner({ commit }, serialNumber) {
    const device = await queryDevice(serialNumber);
    commit("setDevice", device);
    return true;
  },

  async getDeviceEvents({ commit, state }, { deviceId, reload = false, eventTypes = null }) {
    if (reload) {
      commit("setEventsNextPageToken", null);
      commit("setEventsHasNextPage", false);
      commit("setEvents", []);
    }

    commit("setLoadingEvents", true);

    try {
      const nextToken =
        state.eventsNextPageToken && state.eventsNextPageToken !== "" ? state.eventsNextPageToken : null;
      const result = await queryDeviceEvents(deviceId, nextToken, 10, eventTypes);
      if (!result) {
        commit("setEventsError", true);
        return;
      }
      commit("setEventsError", false);

      commit("setEventsHasNextPage", result.hasNextPage);
      commit("setEventsNextPageToken", result.nextPageToken);

      if (reload) {
        commit("setEvents", result.events);
      } else {
        commit("appendEvents", result.events);
      }
      return result;
    } finally {
      commit("setLoadingEvents", false);
    }
  },

  async getDeviceEventsExpertMode(context, { deviceId, nextPageToken = null, take = 40 }) {
    if (!deviceId) {
      throw new Error("deviceId is required");
    }

    const result = await queryDeviceEvents(deviceId, nextPageToken, take, []);
    return result;
  },

  async queryConfigTemplates({ commit, state }, payload) {
    commit("setLoadingConfigTemplates", true);

    const applierId = payload?.applierId !== undefined ? payload.applierId : state.current.owner?.id;

    let hasNextPage = true;
    let nextPageToken = null;
    let configTemplates = [];

    while (hasNextPage) {
      const result = await queryConfigTemplates(applierId, nextPageToken, hasNextPage ? 100 : 80);
      hasNextPage = result.hasNextPage;
      nextPageToken = result.nextPageToken;
      configTemplates = configTemplates.concat(result.templates);
    }
    if (configTemplates && configTemplates.length > 0) configTemplates.sort((a, b) => a.name.localeCompare(b.name));

    commit("setConfigTemplates", configTemplates);
    commit("setLoadingConfigTemplates", false);
  },

  async createConfigTemplate({ commit }, payload) {
    commit("setLoading", true);
    const ret = await createConfigTemplate(payload.template);
    commit("setLoading", false);
    if (ret && ret.template.create.ok) return true;
    return false;
  },

  async updateConfigTemplate({ commit }, payload) {
    commit("setLoading", true);
    const ret = await updateConfigTemplate(payload.template);
    commit("setLoading", false);
    if (ret && ret.template.update.ok) return true;
    return false;
  },

  async deleteConfigTemplate({ commit }, payload) {
    commit("setLoading", true);
    const ret = await deleteConfigTemplate(payload.templateId);
    commit("setLoading", false);
    if (ret && ret.template.delete.ok) return true;
    return false;
  },

  async updateOwner({ commit }, payload) {
    const { deviceId, tenant } = payload;
    const ret = await updateOwner([deviceId], tenant.id);

    if (ret && ret.device?.updateOwner?.ok) {
      commit("setOwner", tenant);
      return true;
    }
    return false;
  },

  async batchUpdateOwner({ commit }, payload) {
    const { deviceIds, tenant } = payload;
    await updateOwner(deviceIds, tenant.id);
    commit("batchSetOwner", payload);
  },

  async updateConfig({ commit, state }, data) {
    commit("setLoading", true);
    commit("setConfig", data);

    const ret = await updateConfigV2(state.current.id ? state.current.id : data.id, data.data);
    commit("setLoading", false);

    if (ret && ret.device?.config?.updateDeviceConfig) return true;
    return false;
  },

  async applyConfigTemplate({ commit }, { templateId, devices, dryRun = false }) {
    if (!devices || devices.length === 0) return { ok: false, results: [] };

    commit("setLoading", true);
    try {
      const ret = await applyConfigTemplate(templateId, devices, dryRun);
      return ret?.template?.apply ?? { ok: false, results: [] };
    } finally {
      commit("setLoading", false);
    }
  },

  async triggerSprinterJob({ commit }, payload) {
    commit("setLoading", true);

    try {
      const ret = await createSprinterJob(payload.things);

      if (ret && ret.createSprinterJob) {
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to trigger sprinter job:", error);
      return false;
    } finally {
      commit("setLoading", false);
    }
  },

  setLoading({ commit }, data) {
    commit("setLoading", data);
  },
};

const mutations = {
  setLoading(state, loading) {
    state.loading = loading;
  },

  setLoadingConfigTemplates(state, loading) {
    state.loadingConfigTemplates = loading;
  },

  setLoadingDevices(state, loading) {
    state.loadingDevices = loading;
  },

  setLoadingAggregations(state, loading) {
    state.loadingAggregations = loading;
  },

  setHasNextPage(state, nextPage) {
    state.hasNextPage = nextPage;
  },

  setNextPageToken(state, pageToken) {
    state.nextPageToken = pageToken;
  },

  setDevices(state, devices) {
    if (devices instanceof Map) {
      state.all = devices;
    } else {
      const newMap = new Map();
      devices.forEach((device) => {
        newMap.set(device.id, device);
      });
      state.all = newMap;
    }
  },

  setDeviceStateAggregations(state, aggregation) {
    state.deviceStateAggregations = aggregation;
  },

  setAlarmAggregations(state, aggregation) {
    state.alarmAggregations = aggregation;
  },

  setDevice(state, device) {
    state.current = { ...device };
  },

  setConfigTemplates(state, configTemplates) {
    state.current.configTemplates = configTemplates;
  },

  setConfig(state, data) {
    state.current.configV2 = data.data;
  },

  setOwner(state, { tenant }) {
    state.current.owner = tenant;
  },

  batchSetOwner(state, { deviceIds, tenant }) {
    deviceIds.forEach((deviceId) => {
      let device = state.all.get(deviceId);
      device.owner = tenant;
    });
  },

  setSearchResult(state, devices) {
    state.searchResult = devices;
  },

  appendDevices(state, devices) {
    devices.forEach((device) => {
      state.all.set(device.id, device);
    });
  },

  setLoadingEvents(state, loading) {
    state.loadingEvents = loading;
  },

  setEventsHasNextPage(state, hasNextPage) {
    state.eventsHasNextPage = hasNextPage;
  },

  setEventsNextPageToken(state, token) {
    state.eventsNextPageToken = token;
  },

  setEvents(state, events) {
    state.events = events;
  },

  appendEvents(state, events) {
    state.events = [...state.events, ...events];
  },

  setEventsError(state, flag) {
    state.eventsError = flag;
  },

  setUptime(state, uptime) {
    state.uptime = uptime;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
};
